import { useCallback, useMemo, useState } from "react";

import type { ResourceType } from "../store/api/resourceTypes";
import {
  type BookmarkItem,
  useResourceBookmarks,
} from "./useResourceBookmarks";

export interface UseTableStarringOptions {
  /** Identifies the kind of resource being starred. */
  resourceType: ResourceType;
  /** Identifies which table owns this starred list; see useResourceBookmarks. */
  cacheId: string;
}

/**
 * Wires up star-toggling and a "starred only" view for a DataTable-backed
 * page, on top of useResourceBookmarks. Give each table its own resourceType
 * + cacheId, the same way ResourceSelector does, and it gets its own
 * localStorage-backed starred list.
 */
export function useTableStarring<T extends BookmarkItem>({
  resourceType,
  cacheId,
}: UseTableStarringOptions) {
  const bookmarks = useResourceBookmarks<T>({ resourceType, cacheId });
  const [starredOnly, setStarredOnly] = useState(false);

  const starColumn = useMemo(
    () => ({
      isStarred: (row: T) => bookmarks.isStarred(row.id),
      onToggle: (row: T) => bookmarks.toggleStar(row),
    }),
    [bookmarks],
  );

  const toggleStarredOnly = useCallback(
    () => setStarredOnly((prev) => !prev),
    [],
  );

  return {
    starColumn,
    starredItems: bookmarks.starredItems,
    starredOnly,
    toggleStarredOnly,
  };
}
